import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { InfoTooltip } from "app/pages/digital-marketing/common/ui/InfoTooltip";

const TRANSLATION_PREFIX = "monitor_and_protect.shared.usage_and_limits_modal";

const StyledHeader = styled(Modal.Header)`
    padding: 20px 24px;
`;

const StyledContent = styled(Modal.Content)`
    & {
        padding: 16px 24px 24px;
    }
`;

const TableGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 120px 120px 120px 1fr;
    width: 100%;
`;

const HeaderCell = styled(Flex)`
    padding: 8px 12px;
`;

const BodyCell = styled(Flex)`
    padding: 11px 12px 12px;
    border-top: 1px solid ${colorsPalettes.carbon["50"]};
    align-items: center;
    min-height: 40px;
`;

const ActionLink = styled.button<{ $color: string }>`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    color: ${({ $color }) => $color};

    &:hover {
        text-decoration: underline;
    }

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`;

interface IUsageRow {
    used: number;
    total: number;
    available: number;
}

interface IUsageAndLimitsModalProps {
    isOpen: boolean;
    onClose: () => void;
    crawls: IUsageRow;
    campaigns: IUsageRow;
    onGetMoreCrawls: () => void;
    onUpgradePackage: () => void;
}

export const UsageAndLimitsModal = ({
    isOpen,
    onClose,
    crawls,
    campaigns,
    onGetMoreCrawls,
    onUpgradePackage,
}: IUsageAndLimitsModalProps) => {
    const { translate } = useTranslate();

    const formatValue = (value: number): string =>
        value === -1 ? translate("monitor_and_protect.limits.unlimited") : value.toLocaleString();

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={{ content: { width: "800px" } }}
            data-automation="mnp-usage-and-limits-modal"
        >
            <StyledHeader>
                <Text
                    fontVariant="h5_2"
                    color={colorsPalettes.carbon["500"]}
                >
                    {translate(`${TRANSLATION_PREFIX}.title`)}
                </Text>
                <Modal.CloseButton
                    onClick={onClose}
                    data-automation="mnp-usage-and-limits-modal-close"
                />
            </StyledHeader>

            <StyledContent>
                <TableGrid>
                    <HeaderCell>
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.column_features`)}
                        </Text>
                    </HeaderCell>
                    <HeaderCell>
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.column_used_overall`)}
                        </Text>
                    </HeaderCell>
                    <HeaderCell>
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.column_in_your_plan`)}
                        </Text>
                    </HeaderCell>
                    <HeaderCell>
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.column_available`)}
                        </Text>
                    </HeaderCell>
                    <HeaderCell>
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.column_action`)}
                        </Text>
                    </HeaderCell>

                    <BodyCell gap="4px">
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.row_keyword_crawls`)}
                        </Text>
                        <InfoTooltip
                            content={translate(`${TRANSLATION_PREFIX}.row_keyword_crawls_tooltip`)}
                            ariaLabel={translate(`${TRANSLATION_PREFIX}.row_keyword_crawls_tooltip`)}
                        />
                    </BodyCell>
                    <BodyCell>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {formatValue(crawls.used)}
                        </Text>
                    </BodyCell>
                    <BodyCell>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {formatValue(crawls.total)}
                        </Text>
                    </BodyCell>
                    <BodyCell>
                        <Text
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {formatValue(crawls.available)}
                        </Text>
                    </BodyCell>
                    <BodyCell>
                        <ActionLink
                            $color={colorsPalettes.blue["400"]}
                            onClick={onGetMoreCrawls}
                            data-automation="mnp-usage-and-limits-modal-get-more-crawls"
                        >
                            {translate(`${TRANSLATION_PREFIX}.action_get_more`)}
                        </ActionLink>
                    </BodyCell>

                    <BodyCell gap="4px">
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {translate(`${TRANSLATION_PREFIX}.row_campaigns`)}
                        </Text>
                        <InfoTooltip
                            content={translate(`${TRANSLATION_PREFIX}.row_campaigns_tooltip`)}
                            ariaLabel={translate(`${TRANSLATION_PREFIX}.row_campaigns_tooltip`)}
                        />
                    </BodyCell>
                    <BodyCell>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {formatValue(campaigns.used)}
                        </Text>
                    </BodyCell>
                    <BodyCell>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {formatValue(campaigns.total)}
                        </Text>
                    </BodyCell>
                    <BodyCell>
                        <Text
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {formatValue(campaigns.available)}
                        </Text>
                    </BodyCell>
                    <BodyCell>
                        <ActionLink
                            $color={colorsPalettes.indigo["300"]}
                            onClick={onUpgradePackage}
                            data-automation="mnp-usage-and-limits-modal-upgrade-package"
                        >
                            {translate(`${TRANSLATION_PREFIX}.action_upgrade_package`)}
                        </ActionLink>
                    </BodyCell>
                </TableGrid>
            </StyledContent>
        </Modal>
    );
};
